import { useCallback, useEffect, useState } from 'react';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

import constants from '../../components/ticketSubmission/definitions/constants';
import { default as useCurrentSupportPlan } from '../useCurrentPlan';
import {
  ChatValidationStatusEnum,
  ChatValidationStatusEnumType,
  RESTRICTED_TICKET_SUBMISSION_LOG_PREFIX,
} from './definitions';
import useUserDetails from '../useUserDetails';
import { isPaidSupportPlan, validateChatSession } from './utils';

const useRestrictTicketSubmit = ({
  chatId,
  sessionId,
}: {
  chatId?: string;
  sessionId?: string;
}): {
  isTicketSubmissionRestricted: boolean;
  isLoading: boolean;
} => {
  const [chatValidationStatus, setChatValidationStatus] =
    useState<ChatValidationStatusEnumType>(
      ChatValidationStatusEnum.UNINITIALIZED,
    );

  const { loading: isLoadingCurrentSupportPlan, supportPlan } =
    useCurrentSupportPlan();

  const { isLoading: isLoadingUserDetails, isLoggedIn } = useUserDetails();

  const currentSupportPlanSid = supportPlan?.billableItemSid;

  const fetchRestrictedTicketSubmissionStatus = useCallback(
    async (chatId: string, sessionId?: string) => {
      try {
        setChatValidationStatus(ChatValidationStatusEnum.LOADING);

        await validateChatSession({
          isLoggedIn,
          sessionId,
          chatId,
        });

        setChatValidationStatus(ChatValidationStatusEnum.VALID);
      } catch (err) {
        const incomingErrorMessage = err?.message ? `::${err?.message}` : '';

        logger.error(
          `${constants.LOG_PREFIX}::${RESTRICTED_TICKET_SUBMISSION_LOG_PREFIX}${incomingErrorMessage}`,
          { isLoggedIn, sessionId, chatId, currentSupportPlanSid },
        );

        setChatValidationStatus(ChatValidationStatusEnum.INVALID);
      }
    },
    [chatId, sessionId, isLoggedIn],
  );

  useEffect(() => {
    /**
     * There's no way to explicitly *know* if the user has an unpaid support plan
     * with our current state management setup. This would allow us to explicitly set the chat validation status to
     * "INVALID" if the user has an unpaid support plan, which would make the enum more intuitive. As of now, we can only infer that the user has an unpaid support plan
     * if the chat validation status is still "UNINITIALIZED" once the user's account details have loaded. Watching the `loading` user account details flags is not reliable
     * because there's a delay between the `loading` flags and the availability of the user's account details returned from the hook.
     */
    if (chatValidationStatus !== ChatValidationStatusEnum.UNINITIALIZED) {
      return;
    }

    if (chatId) {
      fetchRestrictedTicketSubmissionStatus(chatId, sessionId);

      return;
    }

    if (
      isLoggedIn &&
      currentSupportPlanSid &&
      isPaidSupportPlan(currentSupportPlanSid)
    ) {
      setChatValidationStatus(ChatValidationStatusEnum.VALID);

      return;
    }
  }, [
    isLoggedIn,
    currentSupportPlanSid,
    chatValidationStatus,
    fetchRestrictedTicketSubmissionStatus,
  ]);

  return {
    isTicketSubmissionRestricted:
      chatValidationStatus === ChatValidationStatusEnum.INVALID ||
      chatValidationStatus === ChatValidationStatusEnum.UNINITIALIZED,
    isLoading:
      isLoadingCurrentSupportPlan ||
      chatValidationStatus === ChatValidationStatusEnum.LOADING ||
      isLoadingUserDetails,
  };
};

export default useRestrictTicketSubmit;
